import { useMutation, useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { ApiError } from '../../api/client'
import { assignWorkflow, changeTicketTakeover } from '../../api/settingsEaseedeskAssigningWorkflows'
import {
  addFlowStep,
  attachStatusField,
  cloneFormForStatus,
  createFormForStatus,
  createStatusInFlow,
  insertFlowPhase,
  removeFlowStep,
  statusFormQuery,
  statusPaletteQuery,
  type GraphNode,
  type NewStatusFields,
  type SheetResult,
  type SheetStep,
  type StatusFormPage,
  type TicketTypeSheetPage as SheetPage,
} from '../../api/settingsEaseedeskTicketTypeSheet'
import { assignUpdateForm, createUpdateFormHere } from '../../api/settingsEaseedeskUpdateForms'
import { changeStepAssignment, groupUsersQuery } from '../../api/settingsEaseedeskWorkflows'
import { Modal } from '../../ui/Modal'
import { SelectPicker } from '../../ui/SelectPicker'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import { failed, useReload, useSheetMutation } from './sheetHooks'
import { BADGE_W, CARD_H, CARD_W, flowProblems, kindLabel, layoutFlow, type FlowLayout, type FlowProblem } from './ticketTypeFlow'
import './TicketTypeFlowPane.css'

/**
 * Fila "Flow & statuses" a fisei: sanatatea fluxului, fluxul desenat ca graf
 * (fork-uri, intoarceri, redeschideri), statusurile cu formularele lor, si
 * inspectorul statusului ales. Fiecare schimbare se salveaza pe loc.
 */

/** Alb pe culori inchise, negru pe cele deschise (`inkFor` din portal). */
function inkFor(color: string): string {
  const hex = (color || '#777777').replace('#', '')
  if (hex.length < 6) return '#fff'
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)
  return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? '#222' : '#fff'
}

function Pill({ name, color }: { name: string | null; color: string | null }) {
  return (
    <span className="flow-pill" style={{ background: color || '#777', color: inkFor(color || '#777') }}>
      {name}
    </span>
  )
}

function KindTag({ kind }: { kind: number }) {
  return <span className={`flow-kind flow-kind-${kind === 1 ? 'open' : 'final'}`}>{kindLabel(kind)}</span>
}

const EMPTY_STATUS: NewStatusFields = { status_name: '', status_color: '#3c8dbc', status_type: 1, record_sla: 1 }

/** Formularul unui status nou (in flux sau ca faza intre doua statusuri). */
function NewStatusForm({ id, title, onSubmit, busy }: { id: string; title: string; busy: boolean; onSubmit: (fields: NewStatusFields) => void }) {
  const [fields, setFields] = useState<NewStatusFields>(EMPTY_STATUS)
  return (
    <form
      id={id}
      className="flow-new-status"
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit(fields)
      }}
    >
      <input type="text" className="form-control input-sm" name="status_name" placeholder={title} aria-label={title} value={fields.status_name} onChange={(e) => setFields({ ...fields, status_name: e.target.value })} />
      <input type="color" className="form-control input-sm flow-color" name="status_color" aria-label="Color" value={fields.status_color} onChange={(e) => setFields({ ...fields, status_color: e.target.value })} />
      <select className="form-control input-sm" name="status_type" aria-label="Kind" value={fields.status_type} onChange={(e) => setFields({ ...fields, status_type: Number(e.target.value) })}>
        <option value={1}>open</option>
        <option value={2}>final</option>
      </select>
      <label className="flow-check">
        <input type="checkbox" name="record_sla" checked={fields.record_sla === 1} onChange={(e) => setFields({ ...fields, record_sla: e.target.checked ? 1 : 0 })} /> counts for SLA
      </label>
      <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
        <i className="fas fa-plus" aria-hidden="true"></i> add
      </button>
    </form>
  )
}

/** Un `CONFIRM` (409): statusul cu acelasi nume exista deja si e folosit de alte tipuri. */
interface Twin {
  name: string
  usedBy: string[]
  again: () => void
}

function twinOf(error: unknown, again: () => void): Twin | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null
  const d = error.details ?? {}
  return { name: String(d.name ?? ''), usedBy: Array.isArray(d.used_by) ? (d.used_by as string[]) : [], again }
}

export function FlowPane({ data }: { data: SheetPage }) {
  const typeId = data.type.ID
  const reload = useReload(typeId)
  const [picked, setPicked] = useState<number | null>(null)
  const [focusStep, setFocusStep] = useState(0)
  const [twin, setTwin] = useState<Twin | null>(null)
  const [newFormName, setNewFormName] = useState('')

  const workflow = useSheetMutation(typeId, (id: string) => assignWorkflow(typeId, id), () => toast('Workflow assigned.', 'Saved'))
  const takeover = useSheetMutation(typeId, (on: boolean) => changeTicketTakeover(typeId, on))
  const updateForm = useSheetMutation(typeId, (form: number) => assignUpdateForm(typeId, form), () => toast('Update form assigned.', 'Saved'))
  const newUpdateForm = useSheetMutation(typeId, (name: string) => createUpdateFormHere(typeId, name), () => setNewFormName(''))

  const newStatus = useMutation({
    mutationFn: (v: { from: number; fields: NewStatusFields; confirm: boolean }) => createStatusInFlow(typeId, v.from, v.fields, v.confirm),
    onSuccess: (r) => {
      if (r.status === 'ERROR') return toast(r.message ?? 'Could not add the status.', 'Error', 'error')
      setTwin(null)
      toast('Status added to the flow.', 'Saved')
      void reload()
    },
    onError: (error, vars) => {
      const t = twinOf(error, () => newStatus.mutate({ ...vars, confirm: true }))
      if (t) setTwin(t)
      else toast(failed(error), 'Error', 'error')
    },
  })
  const newPhase = useMutation({
    mutationFn: (v: { step: number; fields: NewStatusFields; confirm: boolean }) => insertFlowPhase(typeId, v.step, v.fields, v.confirm),
    onSuccess: (r) => {
      if (r.status === 'ERROR') return toast(r.message ?? 'Could not add the phase.', 'Error', 'error')
      setTwin(null)
      toast('Phase inserted.', 'Saved')
      void reload()
    },
    onError: (error, vars) => {
      const t = twinOf(error, () => newPhase.mutate({ ...vars, confirm: true }))
      if (t) setTwin(t)
      else toast(failed(error), 'Error', 'error')
    },
  })

  const { nodes, edges } = data.graph
  const fieldsOf = useMemo(() => {
    const all = Array.isArray(data.status_fields) ? {} : data.status_fields
    return (status: number) => all[String(status)]?.length ?? 0
  }, [data.status_fields])
  const layout = useMemo(() => layoutFlow(nodes, edges), [nodes, edges])
  const problems = useMemo(() => flowProblems(nodes, edges, fieldsOf), [nodes, edges, fieldsOf])
  const reopens = layout.links.filter((l) => l.reopens)
  const firstStart = edges.find((e) => e.from === 0)?.to ?? nodes[0]?.id ?? 0
  const selected = picked ?? problems[0]?.status ?? firstStart

  const pick = (status: number, step = 0) => {
    setPicked(status)
    setFocusStep(step)
  }
  const lastStatus = data.statuses.length ? data.statuses[data.statuses.length - 1] : null

  return (
    <div className="flow-pane">
      <FlowHealth checks={data.checks} problems={problems} reopens={reopens.length} nodes={nodes} onPick={(id) => pick(id)} />

      <section className="flow-box flow-controls">
        <div>
          <label htmlFor="sheet-workflow" className="flow-label">
            Workflow the tickets follow
          </label>
          <SelectPicker
            id="sheet-workflow"
            name="workflow_id"
            className="form-control input-sm"
            title="Select workflow..."
            options={data.all_workflows.map((w) => ({ value: String(w.ID), label: w.workflow_name }))}
            value={data.workflow_entity ? String(data.workflow_entity.workflow_id) : ''}
            onChange={(v) => workflow.mutate(v)}
          />
          {data.flow_shared_with.length > 0 && <p className="flow-note">Shared with {data.flow_shared_with.join(', ')}: a change here changes them too.</p>}
          <label className="flow-check">
            <input
              type="checkbox"
              id="sheet-takeover"
              checked={(data.workflow_entity?.takeover_button ?? 0) === 1}
              disabled={!data.workflow_entity}
              onChange={(e) => takeover.mutate(e.target.checked)}
            />{' '}
            Agents can take over a ticket
          </label>
        </div>
        <div>
          <label htmlFor="sheet-update-form" className="flow-label">
            Update form <span className="flow-label-soft">shown when the ticket changes</span>
          </label>
          <SelectPicker
            id="sheet-update-form"
            name="form_id"
            className="form-control input-sm"
            title="No update form"
            options={data.update_forms.map((f) => ({ value: String(f.ID), label: f.form_name }))}
            value={data.update_form > 0 ? String(data.update_form) : ''}
            onChange={(v) => updateForm.mutate(Number(v))}
          />
          <form
            className="flow-inline"
            onSubmit={(e) => {
              e.preventDefault()
              if (newFormName.trim()) newUpdateForm.mutate(newFormName.trim())
            }}
          >
            <input type="text" className="form-control input-sm" placeholder="New update form, made here" aria-label="New update form name" value={newFormName} onChange={(e) => setNewFormName(e.target.value)} />
            <button type="submit" className="btn btn-default btn-sm" disabled={newUpdateForm.isPending}>
              Create
            </button>
          </form>
        </div>
      </section>

      <section className="flow-box flow-graph-box">
        <header className="flow-box-head">
          <h5>The flow, as the ticket sees it</h5>
          <span className="flow-note">Click a status or a step to open it on the right. A status can lead to several others.</span>
        </header>
        {nodes.length === 0 ? (
          <p className="flow-note">No statuses yet: add the first one below.</p>
        ) : (
          <FlowCanvas layout={layout} problems={problems} selected={selected} focusStep={focusStep} fieldsOf={fieldsOf} handover={data.has_step_assignment} typeId={typeId} onPick={pick} />
        )}
      </section>

      <FlowInspector key={selected} data={data} status={selected} problems={problems.filter((p) => p.status === selected)} focusStep={focusStep} onPick={pick} onClose={() => pick(0)} newStatus={newStatus} newPhase={newPhase} />

      <section className="flow-box flow-statuses-box">
        <header className="flow-box-head">
          <h5>Statuses and their forms</h5>
          <span className="flow-note">
            {data.statuses.length} status{data.statuses.length === 1 ? '' : 'es'}
          </span>
        </header>
        <div className="flow-table-wrap">
          <table className="table table-hover sheet-statuses">
            <thead>
              <tr>
                <th>Status</th>
                <th>Kind</th>
                <th>Form</th>
                <th className="text-right">Form config</th>
              </tr>
            </thead>
            <tbody>
              {data.statuses.map((status) => {
                const bad = problems.find((p) => p.status === status.ID && (p.kind === 'no_form' || p.kind === 'empty_form'))
                return (
                  <tr key={status.ID} data-status={status.ID} className={selected === status.ID ? 'info' : ''}>
                    <td>
                      <Pill name={status.status_name} color={status.status_color} />
                      {status.shared === 1 && <span className="flow-soft"> shared</span>}
                    </td>
                    <td>
                      <KindTag kind={status.status_type} />
                    </td>
                    <td>
                      <span className="flow-mono">{status.form_name ?? 'no form'}</span>{' '}
                      {bad ? <span className="flow-bad">{bad.title}</span> : status.form_name ? <span className="flow-good">{fieldsOf(status.ID)} fields</span> : null}
                    </td>
                    <td className="text-right">
                      <a href="#" className={`sheet-status-open${selected === status.ID ? ' is-on' : ''}`} onClick={(e) => (e.preventDefault(), pick(status.ID))}>
                        {selected === status.ID ? 'editing now' : 'open'}
                      </a>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="flow-add-last">
          <span className="flow-label">A new status, after {lastStatus ? <em>{lastStatus.status_name}</em> : 'the start'}</span>
          <NewStatusForm id="sheet-new-status" title="Name of the status" busy={newStatus.isPending} onSubmit={(fields) => newStatus.mutate({ from: lastStatus?.ID ?? 0, fields, confirm: false })} />
        </div>
      </section>

      <Modal
        id="sheet-twin"
        open={twin !== null}
        onClose={() => setTwin(null)}
        title="A status with this name already exists"
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setTwin(null)}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" id="sheet-twin-confirm" onClick={() => twin?.again()}>
              Create it anyway
            </button>
          </>
        }
      >
        <p>
          <strong>{twin?.name}</strong> is already a status on this instance
          {twin && twin.usedBy.length > 0 ? <>, used by {twin.usedBy.join(', ')}</> : null}. Creating another one with the same name makes two that look alike.
        </p>
      </Modal>
    </div>
  )
}

function FlowHealth({ checks, problems, reopens, nodes, onPick }: { checks: SheetPage['checks']; problems: FlowProblem[]; reopens: number; nodes: GraphNode[]; onPick: (status: number) => void }) {
  const passed = checks.filter((c) => c.ok).length
  const name = (id: number) => nodes.find((n) => n.id === id)?.name ?? ''
  const bad = problems.length + checks.length - passed
  return (
    <section className={`flow-box flow-health${bad ? ' has-problems' : ''}`} aria-label="Flow health">
      <header className="flow-box-head">
        <h5>
          <i className={`fas ${bad ? 'fa-exclamation-triangle' : 'fa-check-circle'}`} aria-hidden="true"></i> Flow health
        </h5>
        <span className="flow-note">
          {passed} of {checks.length} checks passed · {problems.length} problem{problems.length === 1 ? '' : 's'} on statuses
          {reopens > 0 ? ` · ${reopens} reopen step${reopens === 1 ? '' : 's'}` : ''}
        </span>
      </header>
      <ul className="flow-health-grid">
        {checks.map((c) => (
          <li key={c.label} className={c.ok ? 'is-ok' : 'is-crit'}>
            <i className={`fas ${c.ok ? 'fa-check' : 'fa-times'}`} aria-hidden="true"></i>
            <span>{c.label}</span>
          </li>
        ))}
        {problems.map((p) => (
          <li key={`${p.status}-${p.kind}`} className={p.severity === 'crit' ? 'is-crit' : 'is-warn'}>
            <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
            <span>
              <strong>{p.title}</strong>: {name(p.status)}
              <button type="button" className="flow-link" onClick={() => onPick(p.status)}>
                Fix it →
              </button>
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

function FlowCanvas({
  layout,
  problems,
  selected,
  focusStep,
  fieldsOf,
  handover,
  typeId,
  onPick,
}: {
  layout: FlowLayout
  problems: FlowProblem[]
  selected: number
  focusStep: number
  fieldsOf: (status: number) => number
  handover: boolean
  typeId: number
  onPick: (status: number, step?: number) => void
}) {
  const remove = useSheetMutation(typeId, (step: number) => removeFlowStep(typeId, step))
  const outs = (id: number) => layout.links.filter((l) => l.edge.from === id && l.edge.to !== id).length
  const badges = layout.links.filter((l) => l.kind !== 'start')
  return (
    <div className="flow-canvas-scroll">
      <div className="flow-canvas" data-testid="sheet-graph" style={{ width: layout.width, height: layout.height }}>
        <svg className="flow-lines" width={layout.width} height={layout.height} aria-hidden="true">
          <defs>
            {['line', 'on', 'reopen'].map((m) => (
              <marker key={m} id={`flow-arrow-${m}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M 0 1 L 10 5 L 0 9 z" className={`flow-arrow-${m}`} />
              </marker>
            ))}
          </defs>
          {layout.links.map((l) => {
            const on = l.edge.from === selected || l.edge.to === selected || l.edge.id === focusStep
            const tone = l.reopens ? 'reopen' : on ? 'on' : 'line'
            return <path key={l.edge.id} d={l.path} className={`flow-line flow-line-${l.kind} flow-line-${tone}`} markerEnd={`url(#flow-arrow-${tone})`} />
          })}
        </svg>

        {layout.start && (
          <div className="flow-start" style={{ left: layout.start.x, top: layout.start.y }}>
            <span>start</span>
            <small>{layout.links.find((l) => l.kind === 'start')?.edge.name ?? 'ticket created'}</small>
          </div>
        )}

        {layout.cards.map(({ node, x, y }) => {
          const crit = problems.find((p) => p.status === node.id && p.severity === 'crit')
          const fields = fieldsOf(node.id)
          const ways = outs(node.id)
          return (
            <div
              key={node.id}
              className={`flow-node${selected === node.id ? ' is-selected' : ''}${crit ? ' has-problem' : ''}${node.unreachable ? ' is-unreachable' : ''}`}
              data-status={node.id}
              style={{ left: x, top: y, width: CARD_W, height: CARD_H }}
            >
              <button type="button" className="flow-node-open" onClick={() => onPick(node.id)} aria-label={`Open status ${node.name}`}>
                <span className="flow-node-top">
                  <Pill name={node.name} color={node.color} />
                  <KindTag kind={node.kind} />
                </span>
                <span className="flow-node-form" title={node.form ?? 'no form'}>
                  {node.form ?? 'no form'}
                </span>
                {crit ? (
                  <span className="flow-node-alert">⚠ {crit.title}</span>
                ) : (
                  <span className="flow-node-meta">
                    {node.form ? <span className="flow-good">✓ {fields} field{fields === 1 ? '' : 's'}</span> : <span className="flow-soft">no form</span>}
                    {node.shared && <span className="flow-soft">shared</span>}
                  </span>
                )}
              </button>
              <span className="flow-node-foot">
                <span className="flow-soft">{node.start ? 'first status' : node.kind !== 1 ? 'final' : `${ways} way${ways === 1 ? '' : 's'} out`}</span>
                <button type="button" className="flow-link" onClick={() => onPick(node.id, -1)}>
                  + step to…
                </button>
              </span>
            </div>
          )
        })}

        {badges.map((l) => (
          <div
            key={l.edge.id}
            className={`flow-step flow-step-${l.kind}${l.reopens ? ' is-reopen' : ''}${l.edge.id === focusStep ? ' is-focused' : ''}`}
            data-step={l.edge.id}
            style={{ left: l.label.x, top: l.label.y, maxWidth: BADGE_W }}
          >
            <button type="button" className="flow-step-open" title={l.edge.name} onClick={() => onPick(l.edge.from || l.edge.to, l.edge.id)}>
              <span className="flow-step-name">
                {l.kind === 'back' || l.kind === 'self' ? '↶ ' : ''}
                {l.edge.name}
              </span>
              {l.reopens && <span className="flow-step-tag">final → open</span>}
              {handover && <span className="flow-step-who">→ {l.edge.handed || 'nobody'}</span>}
            </button>
            {l.edge.from > 0 && (
              <button
                type="button"
                className="flow-step-remove"
                aria-label={`Remove step ${l.edge.name}`}
                title="Remove this step"
                onClick={() => {
                  if (window.confirm(`Remove the step "${l.edge.name}"?`)) remove.mutate(l.edge.id)
                }}
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function FlowInspector({
  data,
  status,
  problems,
  focusStep,
  onPick,
  onClose,
  newStatus,
  newPhase,
}: {
  data: SheetPage
  status: number
  problems: FlowProblem[]
  focusStep: number
  onPick: (status: number, step?: number) => void
  onClose: () => void
  newStatus: { mutate: (v: { from: number; fields: NewStatusFields; confirm: boolean }) => void; isPending: boolean }
  newPhase: { mutate: (v: { step: number; fields: NewStatusFields; confirm: boolean }) => void; isPending: boolean }
}) {
  const typeId = data.type.ID
  const node = data.graph.nodes.find((n) => n.id === status)
  const [phaseOn, setPhaseOn] = useState(0)
  const form = useQuery(statusFormQuery(typeId, node ? status : 0))
  const step = useSheetMutation(typeId, (to: number) => addFlowStep(typeId, status, to), () => toast('Step added.', 'Saved'))

  if (!node) {
    return (
      <aside className="flow-box flow-inspector is-empty">
        <p className="flow-note">Click a status in the flow to see where it can go, who gets the ticket, and its form.</p>
      </aside>
    )
  }
  const incoming = data.steps.filter((s) => s.status_to === status)
  const outgoing = data.steps.filter((s) => s.status_from === status)
  const kindOf = (id: number) => data.graph.nodes.find((n) => n.id === id)?.kind ?? 1

  return (
    <aside className="flow-box flow-inspector" data-status={status} aria-label={`Status ${node.name}`}>
      <header className="flow-inspector-head">
        <div>
          <span className="flow-label">Status</span>
          <div className="flow-node-top">
            <Pill name={node.name} color={node.color} />
            <KindTag kind={node.kind} />
            {node.shared && <span className="flow-soft">shared</span>}
          </div>
        </div>
        <button type="button" className="flow-close" aria-label="Close the inspector" onClick={onClose}>
          ×
        </button>
      </header>

      {problems.map((p) => (
        <div key={p.kind} className={`flow-alert flow-alert-${p.severity}`} role="note">
          <strong>⚠ {p.title}</strong>
          <span>{p.text}</span>
        </div>
      ))}

      <section className="flow-inspector-part">
        <h6>
          Comes from <span className="flow-soft">({incoming.length})</span>
        </h6>
        {incoming.map((s) => (
          <StepRow key={s.ID} data={data} step={s} side="from" focused={s.ID === focusStep} reopens={kindOf(s.status_from) !== 1 && node.kind === 1 && s.status_from > 0} onPick={onPick} />
        ))}
        {incoming.length === 0 && <p className="flow-note">No step leads here.</p>}
      </section>

      <section className="flow-inspector-part">
        <h6>
          Where it can go <span className="flow-soft">({outgoing.length})</span>
        </h6>
        {outgoing.map((s) => (
          <div key={s.ID}>
            <StepRow data={data} step={s} side="to" focused={s.ID === focusStep} reopens={node.kind !== 1 && kindOf(s.status_to) === 1} onPick={onPick} onPhase={() => setPhaseOn(phaseOn === s.ID ? 0 : s.ID)} />
            {phaseOn === s.ID && (
              <div className="flow-phase">
                <p className="flow-note">A new status in the middle of this step: the ticket goes through it before reaching {s.to_name}.</p>
                <NewStatusForm id="sheet-new-phase" title="Name of the phase" busy={newPhase.isPending} onSubmit={(fields) => newPhase.mutate({ step: s.ID, fields, confirm: false })} />
              </div>
            )}
          </div>
        ))}
        {outgoing.length === 0 && <p className="flow-note">{node.kind === 1 ? 'Nowhere: the ticket stops here and stays active.' : 'Nowhere: the ticket ends here.'}</p>}
        {(form.data?.candidates.length ?? 0) > 0 && (
          <select className="form-control input-sm sheet-step-to" aria-label="Add a step to" autoFocus={focusStep === -1} defaultValue="" onChange={(e) => e.target.value && step.mutate(Number(e.target.value))}>
            <option value="">+ a step to an existing status…</option>
            {form.data!.candidates.map((c) => (
              <option key={c.ID} value={c.ID}>
                {c.status_name}
              </option>
            ))}
          </select>
        )}
        <details className="flow-more" open={focusStep === -1}>
          <summary>+ a new status, straight from {node.name}</summary>
          <NewStatusForm id="sheet-new-status-from" title="Name of the status" busy={newStatus.isPending} onSubmit={(fields) => newStatus.mutate({ from: status, fields, confirm: false })} />
        </details>
      </section>

      <StatusForm typeId={typeId} status={status} form={form} />
    </aside>
  )
}

function StepRow({
  data,
  step,
  side,
  focused,
  reopens,
  onPick,
  onPhase,
}: {
  data: SheetPage
  step: SheetStep
  side: 'from' | 'to'
  focused: boolean
  reopens: boolean
  onPick: (status: number, step?: number) => void
  onPhase?: () => void
}) {
  const typeId = data.type.ID
  const users = Array.isArray(data.assign_users) ? {} : data.assign_users
  const assignment = useSheetMutation(typeId, (v: { group: number; person: number }) => changeStepAssignment(step.ID, typeId, v.group, v.person))
  const remove = useSheetMutation(typeId, () => removeFlowStep(typeId, step.ID))
  const other = side === 'from' ? step.status_from : step.status_to
  return (
    <div className={`flow-step-row${focused ? ' is-focused' : ''}${reopens ? ' is-reopen' : ''}`} data-step={step.ID}>
      <div className="flow-step-row-head">
        {side === 'to' && <span className="flow-soft">{step.step_name} →</span>}
        {other === 0 ? (
          <em className="flow-soft">start</em>
        ) : (
          <button type="button" className="flow-pill-link" onClick={() => onPick(other)} aria-label={`Open status ${side === 'from' ? step.from_name : step.to_name}`}>
            <Pill name={side === 'from' ? step.from_name : step.to_name} color={side === 'from' ? step.from_color : step.to_color} />
          </button>
        )}
        {side === 'from' && <span className="flow-soft">via {step.step_name}</span>}
        {reopens && <span className="flow-step-tag">reopens the ticket</span>}
        {step.status_from !== 0 && (
          <button
            type="button"
            className="flow-step-remove sheet-step-remove"
            aria-label={`Remove step ${step.step_name}`}
            onClick={() => {
              if (window.confirm(`Remove the step "${step.step_name}"?`)) remove.mutate(undefined)
            }}
          >
            ×
          </button>
        )}
      </div>
      {data.has_step_assignment && (
        <div className="flow-step-row-who">
          <span className="flow-soft">Ticket goes to</span>
          <select
            className="form-control input-sm sheet-step-group"
            aria-label={`Group for step ${step.step_name}`}
            value={step.assign_group_id ?? 0}
            onChange={(e) => assignment.mutate({ group: Number(e.target.value), person: 0 })}
          >
            <option value={0}>- nobody -</option>
            {data.assign_groups.map((g) => (
              <option key={g.ID} value={g.ID}>
                {g.name}
              </option>
            ))}
          </select>
          <StepPerson
            step={step.ID}
            name={step.step_name}
            group={step.assign_group_id ?? 0}
            person={step.assign_user_id ?? 0}
            known={users[String(step.ID)] ?? []}
            onChange={(person) => assignment.mutate({ group: step.assign_group_id ?? 0, person })}
          />
        </div>
      )}
      {onPhase && step.status_from !== 0 && (
        <button type="button" className="flow-link sheet-phase" onClick={onPhase}>
          + phase between
        </button>
      )}
    </div>
  )
}

function StepPerson({ step, name, group, person, known, onChange }: { step: number; name: string; group: number; person: number; known: { ID: number; user_name: string }[]; onChange: (p: number) => void }) {
  const fresh = useQuery({ ...groupUsersQuery(group), enabled: group > 0 && known.length === 0 })
  const people = known.length ? known : (fresh.data?.users ?? [])
  return (
    <select className="form-control input-sm sheet-step-person" aria-label={`Person for step ${name}`} data-step={step} value={person} disabled={group === 0} onChange={(e) => onChange(Number(e.target.value))}>
      <option value={0}>- anyone in the group -</option>
      {people.map((u) => (
        <option key={u.ID} value={u.ID}>
          {u.user_name}
        </option>
      ))}
    </select>
  )
}

/** Formularul statusului: campurile lui, sau copia / formularul nou cand nu are. */
function StatusForm({ typeId, status, form }: { typeId: number; status: number; form: UseQueryResult<StatusFormPage> }) {
  const palette = useQuery({ ...statusPaletteQuery(typeId, status), enabled: !!form.data?.form })
  const [cloneFrom, setCloneFrom] = useState('')
  const [formName, setFormName] = useState('')
  const [piece, setPiece] = useState('')

  const attach = useSheetMutation(typeId, (v: { field: number; custom: boolean }) => attachStatusField(typeId, status, v.field, v.custom), () => setPiece(''))
  const clone = useSheetMutation(typeId, (id: number) => cloneFormForStatus(typeId, status, id), (r) => toast(`Form "${(r as SheetResult).form_name}" created.`, 'Saved'))
  const create = useSheetMutation(typeId, (name: string) => createFormForStatus(typeId, status, name), () => setFormName(''))

  if (form.isError) return <p className="flow-note">{failed(form.error, 'The form could not be read.')}</p>
  if (!form.data) return <Spinner />
  const d = form.data
  const pieces = palette.data?.fields ?? []

  return (
    <section className="flow-inspector-part sheet-status-panel" data-status={status}>
      <h6>
        Form <span className="flow-mono">{d.form ? d.form.form_name : 'none yet'}</span>
      </h6>
      {d.used_by.length > 0 && <p className="flow-note">Also used by {d.used_by.join(', ')}.</p>}
      {d.form ? (
        <>
          <ol className="flow-fields sheet-fields">
            {d.fields.map((f) => (
              <li key={`${f.is_custom}-${f.field_name}`}>
                <span>
                  <strong>{f.field_label || f.field_name}</strong>
                  <span className="flow-mono flow-soft">{f.field_name}</span>
                </span>
                <span className="flow-field-tags">
                  <span className="flow-type">{f.field_type}</span>
                  {f.field_required === 1 ? <span className="flow-bad">required</span> : <span className="flow-soft">optional</span>}
                </span>
              </li>
            ))}
            {d.fields.length === 0 && <li className="flow-bad">No fields: add one, or Save & next into this status fails.</li>}
          </ol>
          <div className="flow-inline">
            <select className="form-control input-sm piece-add" aria-label="Field to add" value={piece} onChange={(e) => setPiece(e.target.value)}>
              <option value="">Choose a field to add…</option>
              {pieces.map((p) => (
                <option key={`${p.custom}-${p.id}`} value={`${p.custom}-${p.id}`}>
                  {p.name} ({p.type})
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-default btn-sm piece-attach"
              disabled={!piece || attach.isPending}
              onClick={() => {
                const [custom, id] = piece.split('-')
                attach.mutate({ field: Number(id), custom: custom === '1' })
              }}
            >
              + Add
            </button>
          </div>
          {(palette.data?.shared_with?.length ?? 0) > 0 && <p className="flow-note">The field lands on {palette.data?.shared_with?.join(', ')} too.</p>}
        </>
      ) : (
        <>
          <div className="flow-inline">
            <select className="form-control input-sm sheet-clone-from" aria-label="Copy an existing form" value={cloneFrom} onChange={(e) => setCloneFrom(e.target.value)}>
              <option value="">Copy an existing form…</option>
              {d.form_list.map((f) => (
                <option key={f.ID} value={f.ID}>
                  {f.form_name}
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-default btn-sm sheet-clone" disabled={!cloneFrom || clone.isPending} onClick={() => clone.mutate(Number(cloneFrom))}>
              Copy
            </button>
          </div>
          <form
            className="flow-inline"
            onSubmit={(e) => {
              e.preventDefault()
              if (formName.trim()) create.mutate(formName.trim())
            }}
          >
            <input type="text" className="form-control input-sm sheet-form-name" placeholder="or a new, empty form" aria-label="New form name" value={formName} onChange={(e) => setFormName(e.target.value)} />
            <button type="submit" className="btn btn-primary btn-sm" disabled={create.isPending}>
              Create
            </button>
          </form>
        </>
      )}
      {d.shared_with.length > 0 && <p className="flow-note">The workflow is shared with {d.shared_with.join(', ')}.</p>}
    </section>
  )
}
